import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue";
const _hoisted_1 = { class: "flex items-center justify-between px-5 py-3 border-b-2 border-(--border-border-base-soft) bg-white" };
const _hoisted_2 = { class: "text-sm text-(--text-text-body)" };
const _hoisted_3 = { class: "px-5 py-3 bg-white" };
import { Badge, Button, Checkbox, DataTable, Dropdown, Modal, Pager, RadioButton, TablePanel, TableShell, useTableQuery, useTableViews, } from "@eudonet/opal";
import { Fragment, computed, h, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { addressBadgeTheme } from "../../domain/address-utils";
import { useAddressFormatter } from "@/shared/composables/useAddressFormatter";
import { buildTableLabels } from "@/shared/composables/tableLabels";
export default /*@__PURE__*/ _defineComponent({
    __name: 'AddressesExpandModal',
    props: {
        modelValue: { type: Boolean },
        addresses: {},
        primaryId: {},
        canEdit: { type: Boolean, default: false }
    },
    emits: ["update:modelValue", "update:primaryId", "edit", "delete"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const { t, locale } = useI18n();
        const tableLabels = computed(() => buildTableLabels(t));
        const addressFormatter = useAddressFormatter();
        const localPrimaryId = ref(props.primaryId);
        watch(() => props.primaryId, (val) => {
            localPrimaryId.value = val;
        });
        // ---------------------------------------------------------------------------
        // TableShell: query + views (tabs)
        // ---------------------------------------------------------------------------
        const tableQuery = useTableQuery({ activeViewId: "all", pageSize: 10 });
        const initialViews = [
            {
                id: "all",
                label: t("organisation.detail.addresses.modal.allAddresses"),
                count: props.addresses.length,
                isPreset: true,
                isDefault: true,
                querySnapshot: {},
            },
        ];
        const tableViews = useTableViews({ initialViews });
        watch([() => props.addresses.length, locale], ([count]) => tableViews.updateView("all", { label: t("organisation.detail.addresses.modal.allAddresses"), count }));
        // ---------------------------------------------------------------------------
        // Pagination
        // ---------------------------------------------------------------------------
        const totalPages = computed(() => Math.max(1, Math.ceil(props.addresses.length / tableQuery.state.pageSize)));
        const paginatedAddresses = computed(() => {
            const start = (tableQuery.state.page - 1) * tableQuery.state.pageSize;
            return props.addresses.slice(start, start + tableQuery.state.pageSize);
        });
        // ---------------------------------------------------------------------------
        // DataTable columns
        // ---------------------------------------------------------------------------
        const columns = computed(() => {
            const base = [
                {
                    id: "select",
                    header: ({ table }) => h(Checkbox, {
                        modelValue: table.getIsAllPageRowsSelected(),
                        indeterminate: table.getIsSomePageRowsSelected(),
                        "onUpdate:modelValue": (val) => table.toggleAllPageRowsSelected(val),
                        ariaLabel: t("table.selectAll"),
                    }),
                    cell: ({ row }) => h(Checkbox, {
                        modelValue: row.getIsSelected(),
                        "onUpdate:modelValue": (val) => row.toggleSelected(val),
                        ariaLabel: t("table.selectRow"),
                    }),
                    draggable: false,
                    resizable: false,
                    width: 40,
                    minWidth: 40,
                    maxWidth: 40,
                },
                {
                    id: "fullAddress",
                    header: t("organisation.detail.addresses.columns.fullAddress"),
                    cell: ({ row }) => h("span", { class: "text-sm text-(--text-text-black-default) truncate block" }, addressFormatter.format(row.original)),
                    minWidth: 200,
                },
                {
                    id: "type",
                    header: t("organisation.detail.addresses.columns.type"),
                    cell: ({ row }) => h(Badge, {
                        theme: addressBadgeTheme(row.original.type),
                        label: t(`organisation.detail.addresses.types.${row.original.type}`),
                        showCloseIcon: false,
                        size: "sm",
                        truncate: true,
                    }),
                    width: 160,
                    minWidth: 160,
                    maxWidth: 160,
                },
                {
                    id: "isPrimary",
                    header: t("organisation.detail.addresses.columns.isPrimary"),
                    cell: ({ row }) => h(Fragment, null, [
                        h("label", {
                            for: `radio-primary-modal-${row.original.id}`,
                            class: "sr-only",
                        }, t("organisation.detail.addresses.columns.setPrimaryAriaLabel", {
                            address: addressFormatter.format(row.original),
                        })),
                        h(RadioButton, {
                            id: `radio-primary-modal-${row.original.id}`,
                            modelValue: localPrimaryId.value,
                            value: row.original.id,
                            name: "primary-address-modal",
                            disabled: !props.canEdit,
                            "onUpdate:modelValue": (id) => {
                                localPrimaryId.value = id;
                                emit("update:primaryId", id);
                            },
                        }),
                    ]),
                    width: 120,
                    minWidth: 120,
                    maxWidth: 120,
                },
            ];
            if (props.canEdit) {
                base.push({
                    id: "actions",
                    header: "",
                    noTruncate: true,
                    cell: ({ row }) => h(Dropdown, {
                        ghost: true,
                        label: "",
                        triggerIconLeft: "ri-more-line",
                        menuAlign: "right",
                        ariaLabel: t("organisation.detail.addresses.rowActions"),
                        items: [
                            {
                                label: t("organisation.detail.addresses.actions.edit"),
                                icon: "ri-pencil-line",
                                onClick: () => emit("edit", row.original),
                            },
                            {
                                label: t("organisation.detail.addresses.actions.delete"),
                                icon: "ri-delete-bin-line",
                                danger: true,
                                onClick: () => emit("delete", row.original),
                            },
                        ],
                    }),
                    width: 56,
                    minWidth: 56,
                    maxWidth: 56,
                    draggable: false,
                    resizable: false,
                });
            }
            return base;
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_unref(Modal), {
                "model-value": __props.modelValue,
                size: "xl",
                "show-close-button": true,
                "show-header": false,
                "footer-position": "right",
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => (_ctx.$emit('update:modelValue', $event)))
            }, {
                footer: _withCtx(() => [
                    _createVNode(_unref(Button), {
                        variant: "secondary",
                        label: _ctx.$t('organisation.detail.addresses.modal.close'),
                        onClick: _cache[2] || (_cache[2] = ($event) => (_ctx.$emit('update:modelValue', false)))
                    }, null, 8 /* PROPS */, ["label"])
                ]),
                default: _withCtx(() => [
                    _createVNode(_unref(TableShell), {
                        query: _unref(tableQuery),
                        views: _unref(tableViews),
                        labels: tableLabels.value,
                        "show-search": false,
                        "show-controls": false,
                        "show-view-selector": false,
                        class: "[&>div>div:first-child]:bg-transparent!"
                    }, {
                        table: _withCtx(() => [
                            _createVNode(_unref(TablePanel), { "round-top": false }, {
                                default: _withCtx(() => [
                                    _createElementVNode("div", _hoisted_1, [
                                        _createElementVNode("p", _hoisted_2, _toDisplayString(_ctx.$t("organisation.detail.addresses.modal.count", { count: __props.addresses.length })), 1 /* TEXT */),
                                        _createVNode(_unref(Button), {
                                            variant: "secondary",
                                            size: "sm",
                                            label: _ctx.$t('organisation.detail.addresses.modal.export'),
                                            "icon-left": "ri-file-transfer-line",
                                            disabled: true
                                        }, null, 8 /* PROPS */, ["label"])
                                    ]),
                                    _createVNode(_unref(DataTable), {
                                        columns: columns.value,
                                        data: paginatedAddresses.value,
                                        "empty-text": _ctx.$t('organisation.detail.addresses.modal.empty')
                                    }, null, 8 /* PROPS */, ["columns", "data", "empty-text"]),
                                    _createElementVNode("div", _hoisted_3, [
                                        _createVNode(_unref(Pager), {
                                            "current-page": _unref(tableQuery).state.page,
                                            "total-pages": totalPages.value,
                                            "total-items": __props.addresses.length,
                                            "items-per-page": _unref(tableQuery).state.pageSize,
                                            "show-items-count": "",
                                            "show-go-to-page": "",
                                            "items-count-separator": _ctx.$t('pagination.of'),
                                            "previous-label": _ctx.$t('pagination.previous'),
                                            "next-label": _ctx.$t('pagination.next'),
                                            "go-to-page-label": _ctx.$t('pagination.goToPage'),
                                            "go-label": _ctx.$t('pagination.go'),
                                            onPageChange: _cache[0] || (_cache[0] = ($event) => (_unref(tableQuery).setPage($event))),
                                            onItemsPerPageChange: _cache[1] || (_cache[1] = ($event) => (_unref(tableQuery).setPageSize($event)))
                                        }, null, 8 /* PROPS */, ["current-page", "total-pages", "total-items", "items-per-page", "items-count-separator", "previous-label", "next-label", "go-to-page-label", "go-label"])
                                    ])
                                ]),
                                _: 1 /* STABLE */
                            })
                        ]),
                        _: 1 /* STABLE */
                    }, 8 /* PROPS */, ["query", "views", "labels"])
                ]),
                _: 1 /* STABLE */
            }, 8 /* PROPS */, ["model-value"]));
        };
    }
});
